import { getAPIBaseUrl } from '@/app/platform/core/env.ts';
import i18n from '@/app/platform/core/i18n';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { formatApiDetail } from './apiDetail.ts';
import { authService } from './authentication-service.ts';
import { buildTechnicalDetail, setSessionEndReason } from './sessionEndReason.ts';
import { tokenManager } from './tokenManager.ts';
import { useProactiveTokenRefresh } from './useProactiveTokenRefresh.ts';

interface User {
  id: string;
  email: string;
  roles: string[];
  permissions: string[];
  is_active: boolean;
  language: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (accessToken: string, refreshToken: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(
    tokenManager.getAccessToken(),
  );
  const [isLoading, setIsLoading] = useState(true);
  const hasFetched = useRef(false);

  const fetchUser = useCallback(
    async (accessToken: string, allowRefresh = true) => {
      try {
        const response = await fetch(`${getAPIBaseUrl()}/platform/core/authentication/me`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
          if (userData.language) {
            i18n.changeLanguage(userData.language);
            localStorage.setItem('user_language', userData.language);
          }
          return;
        }
        if (response.status === 401 || response.status === 403) {
          const failureDetail: string | null = await response
            .json()
            .then((d) => formatApiDetail(d?.detail))
            .catch(() => null);
          if (allowRefresh) {
            const newToken = await tokenManager.tryRefresh();
            if (newToken) {
              await fetchUser(newToken, false);
              return;
            }
          }
          // Token definitively rejected and refresh unavailable — clear session. A refresh
          // attempt just above may already have recorded the more specific reason the
          // *refresh itself* was rejected for (see doRefresh's catch below); this is only
          // the fallback when no refresh was attempted or available.
          setSessionEndReason(
            failureDetail || 'Your session is no longer valid.',
            true,
            buildTechnicalDetail('GET /platform/core/authentication/me', response.status, null),
          );
          tokenManager.clearAll();
          setToken(null);
          return;
        }
        // Any other status (5xx, etc.) is a transient server/infra issue — e.g. a redeploy
        // briefly returning 502/503 — not proof the session is invalid, so keep the tokens
        // and let the next request retry instead of forcing a re-login.
      } catch {
        // Network error or CORS failure — token may still be valid.
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  // Called by tokenManager when api.service.ts encounters a 401.
  const doRefresh = useCallback(async (): Promise<string | null> => {
    const storedRefresh = tokenManager.getRefreshToken();
    if (!storedRefresh) return null;
    try {
      const data = await authService.refreshToken(storedRefresh);
      tokenManager.setAccessToken(data.access_token);
      tokenManager.setRefreshToken(data.refresh_token);
      setToken(data.access_token);
      return data.access_token;
    } catch (err) {
      // Only clear the session on a definitive auth rejection (401/403).
      // Network errors and 5xx are transient — keep tokens so the next attempt can retry.
      const status = (err as Error & { status?: number }).status;
      if (status === 401 || status === 403) {
        // Another tab may have rotated the refresh token while this request was in flight: its
        // session is fine, so adopt the tokens it stored instead of ending the session.
        if (tokenManager.getRefreshToken() !== storedRefresh) return tokenManager.getAccessToken();
        // This is the most specific reason available (the refresh endpoint's own
        // rejection, e.g. "Refresh token expired" / "Refresh token revoked") — it always
        // wins over the plain "session is no longer valid" fallback set elsewhere.
        const code = (err as Error & { code?: string | null }).code ?? null;
        setSessionEndReason(
          (err as Error).message || 'Your session is no longer valid.',
          false,
          buildTechnicalDetail('POST /platform/core/authentication/refresh', status, code),
        );
        tokenManager.clearAll();
        setToken(null);
        setUser(null);
      }
      return null;
    }
  }, []);

  useProactiveTokenRefresh(token);

  // Ask the browser not to evict the stored tokens (low disk space, Safari's inactivity purge).
  useEffect(() => {
    tokenManager.requestPersistentStorage();
  }, []);

  useEffect(() => {
    tokenManager.setRefresher(doRefresh);
  }, [doRefresh]);

  // Sync auth state when another tab updates localStorage (token refresh or logout).
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key !== 'access_token') return;
      const newToken = e.newValue;
      setToken(newToken);
      if (!newToken) {
        setUser(null);
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const accessToken = tokenManager.getAccessToken();
    if (accessToken) {
      fetchUser(accessToken);
      return;
    }

    // Access token absent or expired — try to restore session via refresh token.
    const refreshToken = tokenManager.getRefreshToken();
    if (refreshToken) {
      tokenManager.tryRefresh().then((newToken) => {
        if (newToken) {
          fetchUser(newToken);
        } else {
          setIsLoading(false);
        }
      });
    } else {
      setIsLoading(false);
    }
  }, [fetchUser, doRefresh]);

  const login = useCallback(
    async (accessToken: string, refreshToken: string) => {
      tokenManager.setAccessToken(accessToken);
      tokenManager.setRefreshToken(refreshToken);
      setToken(accessToken);
      await fetchUser(accessToken);
    },
    [fetchUser],
  );

  const logout = async () => {
    const currentToken = tokenManager.getAccessToken();
    if (currentToken) {
      try {
        await fetch(`${getAPIBaseUrl()}/platform/core/authentication/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${currentToken}` },
        });
      } catch {
        // best-effort
      }
    }
    tokenManager.clearAll();
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        login,
        logout,
        isAuthenticated: !!user,
        isLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
